import { useState } from 'react';
import { X, Save, Type, Clock, Loader2, Film } from 'lucide-react';
import toast from 'react-hot-toast';
import { cleanText } from '@/utils/sanitize';
import { courseService } from '@/services/course.service';
import { ContentItem } from '@/types/course.types';

interface EditVideoContentItemModalProps {
  contentItem: ContentItem;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Minimal editor for an uploaded-video content item (video_presentation with a
 * directly uploaded video, no scenes). The video is fixed — only the title and
 * the estimated time are editable here (no narration / audio / video sections).
 */
export default function EditVideoContentItemModal({ contentItem, onClose, onSuccess }: EditVideoContentItemModalProps) {
  const [title, setTitle] = useState(contentItem.title || '');
  const [hours, setHours] = useState<string>(String(contentItem.estimated_time?.hours ?? 0));
  const [minutes, setMinutes] = useState<string>(String(contentItem.estimated_time?.minutes ?? 0));
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setIsSaving(true);
    const loading = toast.loading('Saving...');
    try {
      await courseService.updateContentItem(contentItem._id!, {
        title: cleanText(title),
        estimated_time: {
          hours: Math.max(0, Math.floor(Number(hours) || 0)),
          minutes: Math.max(0, Math.min(59, Math.floor(Number(minutes) || 0))),
        },
      });
      toast.success('Content updated', { id: loading });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update content', { id: loading });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" dir="ltr">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-gradient-to-br from-brand-purple-500 to-brand-indigo-600 rounded-lg flex items-center justify-center">
              <Film className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">Edit Video Content</h2>
          </div>
          <button onClick={onClose} disabled={isSaving} className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Title */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
              <Type className="w-3.5 h-3.5 text-gray-400" />
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Content title..."
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-500 transition-all"
            />
          </div>

          {/* Estimated Time */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              Estimated Time
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="0"
                  className="w-full px-3.5 py-2.5 pr-14 border border-gray-200 rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-500 transition-all"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">hours</span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  placeholder="0"
                  className="w-full px-3.5 py-2.5 pr-14 border border-gray-200 rounded-lg text-sm bg-gray-50/50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-500 transition-all"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">mins</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button onClick={onClose} disabled={isSaving} className="px-4 py-2 text-sm border border-gray-200 text-gray-600 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 text-sm bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-semibold"
          >
            {isSaving ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>) : (<><Save className="w-3.5 h-3.5" /> Save</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
